import React, { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "../../components/ui/button";
import { DifficultySelector, GameOverScreen } from "./GameShell";
import { Check } from "lucide-react";
import { toast } from "sonner";

const WORD_BANK = [
  "mountain","ocean","sunset","garden","castle","forest","river","desert",
  "thunder","crystal","shadow","autumn","breeze","island","temple","dragon",
  "mirror","silver","meadow","canyon","glacier","volcano","comet","falcon",
  "anchor","puzzle","beacon","voyage","harvest","legend","marble","orchid"
];

const DIFFICULTY_CONFIG = {
  easy:   { startWords: 4, maxLevel: 4, timePerWord: 3, bonusMult: 1 },
  medium: { startWords: 4, maxLevel: 5, timePerWord: 2, bonusMult: 1.5 },
  hard:   { startWords: 5, maxLevel: 5, timePerWord: 1.5, bonusMult: 2 },
};

const TUTORIAL_STEPS = [
  { visual: "📖", title: "Memorize Words", description: "A list of words will appear on screen. Study them carefully during the timer." },
  { visual: "🧠", title: "Recall Phase", description: "Once the timer ends, type back as many words as you remember." },
  { visual: "⭐", title: "Perfect Recall", description: "Recall all words to earn a bonus and advance to the next level with more words!" },
];

const WordRecallGame = ({ onComplete, onBack, t }) => {
  const [difficulty, setDifficulty] = useState(null);
  const [phase, setPhase] = useState("memorize");
  const [words, setWords] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [recalled, setRecalled] = useState([]);
  const [timeLeft, setTimeLeft] = useState(0);
  const [level, setLevel] = useState(1);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [config, setConfig] = useState(null);
  const inputRef = useRef(null);
  const configRef = useRef(null);

  const startLevel = useCallback((lvl, cfg) => {
    const count = Math.min(cfg.startWords + lvl - 1, 12);
    const shuffled = [...WORD_BANK].sort(() => Math.random() - 0.5);
    setWords(shuffled.slice(0, count));
    setRecalled([]);
    setUserInput("");
    setPhase("memorize");
    setTimeLeft(Math.round(count * cfg.timePerWord));
  }, []);

  const startGame = useCallback((diff) => {
    const cfg = DIFFICULTY_CONFIG[diff];
    setDifficulty(diff);
    setConfig(cfg);
    configRef.current = cfg;
    setLevel(1);
    setScore(0);
    setGameOver(false);
    startLevel(1, cfg);
  }, [startLevel]);

  // Memorize timer
  useEffect(() => {
    if (phase !== "memorize" || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) { setPhase("recall"); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, timeLeft]);

  // Focus input on recall phase
  useEffect(() => {
    if (phase === "recall") {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [phase]);

  const submitWord = () => {
    const word = userInput.trim().toLowerCase();
    if (!word) return;
    const cfg = configRef.current;

    if (words.includes(word) && !recalled.includes(word)) {
      const pts = Math.round(15 * cfg.bonusMult);
      setRecalled((prev) => [...prev, word]);
      setScore((s) => s + pts);

      if (recalled.length + 1 === words.length) {
        const bonus = Math.round(50 * cfg.bonusMult);
        setScore((s) => s + bonus);
        if (level < cfg.maxLevel) {
          setLevel((l) => l + 1);
          toast.success(t("games.levelComplete", { level }));
          setTimeout(() => startLevel(level + 1, cfg), 800);
        } else {
          setGameOver(true);
          const finalScore = score + pts + bonus;
          const accuracy = Math.round(((recalled.length + 1) / words.length) * 100);
          onComplete(finalScore, level, accuracy, difficulty);
        }
      }
    }
    setUserInput("");
  };

  const giveUp = () => {
    setGameOver(true);
    const accuracy = words.length > 0 ? Math.round((recalled.length / words.length) * 100) : 0;
    onComplete(score, level, accuracy, difficulty);
  };

  if (!difficulty) {
    return (
      <DifficultySelector
        gameName={t("games.wordRecall.name") || "Word Recall"}
        gameDescription={t("games.wordRecall.desc") || "Memorize and recall lists of words"}
        tutorialSteps={TUTORIAL_STEPS}
        onStart={startGame}
        onBack={onBack}
        t={t}
      />
    );
  }

  if (gameOver) {
    return (
      <GameOverScreen
        score={score}
        subtitle={`${recalled.length}/${words.length} ${t("games.wordRecall.wordsRecalled")} | ${t("games.difficulty." + difficulty)}`}
        onPlayAgain={() => startGame(difficulty)}
        onBack={onBack}
        t={t}
      >
        <div className="flex flex-wrap gap-2 justify-center mt-2">
          {words.map((w, i) => (
            <span
              key={`result-${w}-${i}`}
              className={`px-3 py-1 rounded-full text-sm ${
                recalled.includes(w) ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
              }`}
            >
              {w}
            </span>
          ))}
        </div>
      </GameOverScreen>
    );
  }

  return (
    <div className="text-center" data-testid="word-game-active">
      <div className="flex justify-between mb-6">
        <div>
          <p className="text-sm text-slate-500">{t("common.level")}</p>
          <p className="text-xl font-medium text-sky-800">{level}/{configRef.current?.maxLevel}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">{t("common.score")}</p>
          <p className="text-xl font-medium text-sky-800">{score}</p>
        </div>
        <div>
          <p className="text-sm text-slate-500">{t("games.wordRecall.recalled")}</p>
          <p className="text-xl font-medium text-sky-800">{recalled.length}/{words.length}</p>
        </div>
      </div>

      {phase === "memorize" && (
        <div>
          <p className="text-sky-500 mb-4 animate-pulse">
            {t("games.wordRecall.memorize")} ({timeLeft}s)
          </p>
          <div className="glass-card p-6 mb-4">
            <div className="flex flex-wrap gap-3 justify-center">
              {words.map((word, i) => (
                <span
                  key={`mem-${word}-${i}`}
                  className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-xl text-lg font-medium"
                >
                  {word}
                </span>
              ))}
            </div>
          </div>
          <Button onClick={() => { setPhase("recall"); setTimeLeft(0); }} variant="outline">
            {t("games.wordRecall.ready")}
          </Button>
        </div>
      )}

      {phase === "recall" && (
        <div>
          <p className="text-slate-600 mb-4">{t("games.wordRecall.typeWords")}</p>
          <div className="flex gap-2 max-w-md mx-auto mb-6">
            <input
              ref={inputRef}
              type="text"
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitWord()}
              placeholder={t("games.wordRecall.placeholder")}
              className="flex-1 text-center text-lg p-3 border-2 border-sky-200 rounded-xl focus:border-sky-400 outline-none"
              autoFocus
              data-testid="word-input"
            />
            <Button onClick={submitWord} className="glow-button" data-testid="word-submit-btn">
              <Check className="w-5 h-5" />
            </Button>
          </div>

          {recalled.length > 0 && (
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {recalled.map((w, i) => (
                <span key={`rec-${w}-${i}`} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  {w}
                </span>
              ))}
            </div>
          )}

          <Button variant="outline" onClick={giveUp} className="text-slate-500" data-testid="give-up-btn">
            {t("games.wordRecall.done")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default WordRecallGame;
